import { Stack } from '@mantine/core';
import { useApiGetErdFlow } from '@repo/api-codegen';
import { ReactFlow } from '@xyflow/react';
import { observer } from 'mobx-react-lite';
import { forwardRef } from 'react';
import { useERDFlowContext } from '../../context/ERDFlowProvider/ERDFlowContext';
import Controls from '../Controls/Controls';
import { useStyles } from '../LineageFlow/LineageFlow.styles';
import LineageFlowContainer from '../LineageFlow/LineageFlowContainer';
import { edgeTypes, nodeTypes, type ERDFlowProps } from './ERDFlow.types';

import '@xyflow/react/dist/base.css';
import { useDeepCompareEffect } from 'ahooks';

const ERDFlow = forwardRef<HTMLDivElement, ERDFlowProps>((props, ref) => {
	const {
		reactFlowInstance,
		rootId: rootID,
		nodes,
		edges,
		isFullscreen,
		onInitReactFlow,
		onInitData,
		onExitFullscreen,
		onNodesChange,
		onEdgesChange,
		onNodeClick,
		onEdgeClick,
		onEdgeMouseEnter,
		onEdgeMouseLeave,
	} = useERDFlowContext();

	const { classes } = useStyles({ isFullscreen });

	const { data } = useApiGetErdFlow({
		pathParams: {
			tableId: rootID,
		},
	});

	useDeepCompareEffect(() => {
		if (data) {
			onInitData({ data });
		}
	}, [data, onInitData]);

	return (
		<LineageFlowContainer
			isFullscreen={isFullscreen}
			exitFullscreen={onExitFullscreen}
			isERD
		>
			<Stack className={classes.wrapper}>
				<ReactFlow
					ref={ref}
					className={classes.reactFlow}
					nodes={nodes}
					edges={edges}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					onInit={onInitReactFlow}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					onNodeClick={onNodeClick}
					onEdgeClick={onEdgeClick}
					onEdgeMouseEnter={onEdgeMouseEnter}
					onEdgeMouseLeave={onEdgeMouseLeave}
					snapToGrid={true}
					minZoom={0.25}
					maxZoom={1.5}
					panOnScroll
					fitView
				>
					<Controls reactFlowInstance={reactFlowInstance as any} />
				</ReactFlow>
			</Stack>
		</LineageFlowContainer>
	);
});

const ObservedERDFlow = observer(ERDFlow);

ERDFlow.displayName = 'ERDFlow';
ObservedERDFlow.displayName = 'ObservedERDFlow';

export default ObservedERDFlow;
